import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
const _excluded = ["planIds", "navigateToSectionProps", "navigateToPageProps", "biOptions", "verticalStatusContent", "restrictedPageId", "restrictedPagePath"],
  _excluded2 = ["planIds", "navigateToPageProps", "navigateToSectionProps", "biOptions", "verticalStatusContent"];
import { decodeBase64, decodeBase64Url, encodeBase64Url, encodeBase64 } from './encode-base64';
import { AppSectionParamsDecodeError, toError } from './errors';
export function extractIntegrationData(appSectionParams) {
  const planIds = appSectionParams.planIds,
    navigateToSectionProps = appSectionParams.navigateToSectionProps,
    navigateToPageProps = appSectionParams.navigateToPageProps,
    biOptions = appSectionParams.biOptions,
    verticalStatusContent = appSectionParams.verticalStatusContent,
    restrictedPageId = appSectionParams.restrictedPageId,
    restrictedPagePath = appSectionParams.restrictedPagePath,
    rest = _objectWithoutPropertiesLoose(appSectionParams, _excluded);
  const decodingErrors = [];
  function collectErrors(action) {
    return (input, fieldName) => {
      try {
        return action(input);
      } catch (error) {
        decodingErrors.push([fieldName, input + ' ' + toError(error)]);
        return;
      }
    };
  }
  const safeAtob = collectErrors(atob);
  const safeDecode = collectErrors(decodeBase64);
  const integrationData = {
    ...rest,
    navigateToPageProps: navigateToPageProps && safeAtob(navigateToPageProps, 'navigateToPageProps'),
    navigateToSectionProps: navigateToSectionProps && safeDecode(navigateToSectionProps, 'navigateToSectionProps'),
    verticalStatusContent: verticalStatusContent && safeDecode(verticalStatusContent, 'verticalStatusContent'),
    biOptions: biOptions ? safeDecode(biOptions, 'biOptions') : undefined,
    planIds: planIds ? planIds.split(',') : undefined
  };
  return {
    integrationData,
    decodingErrors: decodingErrors.length > 0 ? Object.fromEntries(decodingErrors) : undefined
  };
}
export function extractRestrictedPageParams(appSectionParams) {
  if (appSectionParams.restrictedPageId || appSectionParams.restrictedPagePath) {
    return {
      restrictedPageId: appSectionParams.restrictedPageId,
      restrictedPagePath: appSectionParams.restrictedPagePath
    };
  }
  return undefined;
}
export function parseAppSectionParams(value) {
  try {
    return value ? JSON.parse(value) : {};
  } catch (e) {
    throw new AppSectionParamsDecodeError(toError(e).message);
  }
}
export function integrationDataFromQuery(query) {
  const navigateTo = query.get('destination') ?? undefined;
  const navigateToPageProps = query.get('page') ?? undefined;
  const section = query.get('section') ?? undefined;
  const navigateToSectionProps = section ? decodeBase64Url(section) : undefined;
  const referralInfo = query.get('referral') ?? undefined;
  const planIds = query.get('planIds');
  return {
    title: query.get('title') ?? undefined,
    subtitle: query.get('subtitle') ?? undefined,
    planIds: typeof planIds === 'string' ? planIds.split(',') : undefined,
    navigateTo,
    navigateToPageProps,
    navigateToSectionProps,
    verticalStatusContent: readVerticalStatusContent(query),
    biOptions: referralInfo ? {
      referralInfo,
      referralId: query.get('referralId') ?? undefined
    } : undefined,
    minStartDate: query.get('minStartDate') ?? undefined,
    maxStartDate: query.get('maxStartDate') ?? undefined
  };
}
export function commonIntegrationDataToQuery(data) {
  const query = new URLSearchParams();
  data.navigateTo && query.set('destination', data.navigateTo);
  data.navigateToPageProps && query.set('page', data.navigateToPageProps);
  data.navigateToSectionProps && query.set('section', encodeBase64Url(data.navigateToSectionProps));
  if (data.biOptions) {
    const _data$biOptions = data.biOptions,
      referralInfo = _data$biOptions.referralInfo,
      referralId = _data$biOptions.referralId;
    referralInfo && query.set('referral', referralInfo);
    referralId && query.set('referralId', referralId);
  }
  data.maxStartDate && query.set('maxStartDate', data.maxStartDate);
  data.minStartDate && query.set('minStartDate', data.minStartDate);
  if (data.verticalStatusContent) {
    const _data$verticalStatusC = data.verticalStatusContent,
      titleText = _data$verticalStatusC.titleText,
      contentText = _data$verticalStatusC.contentText,
      buttonText = _data$verticalStatusC.buttonText;
    titleText && query.set('thankYouTitle', titleText);
    contentText && query.set('thankYouMessage', contentText);
    buttonText && query.set('thankYouCTA', buttonText);
  }
  return query;
}
export function integrationDataToQuery(data) {
  const query = commonIntegrationDataToQuery(data);
  data.planIds && query.set('planIds', data.planIds.join(','));
  data.title && query.set('title', data.title);
  data.subtitle && query.set('subtitle', data.subtitle);
  return query;
}
function readVerticalStatusContent(query) {
  const verticalTitleText = query.get('thankYouTitle') ?? undefined;
  const verticalContentText = query.get('thankYouMessage') ?? undefined;
  const verticalButtonText = query.get('thankYouCTA') ?? undefined;
  return verticalTitleText || verticalContentText || verticalButtonText ? {
    titleText: verticalTitleText,
    contentText: verticalContentText,
    buttonText: verticalButtonText
  } : undefined;
}
export function integrationDataToPricingPageOptions(integrationData) {
  const title = integrationData.title,
    subtitle = integrationData.subtitle,
    planIds = integrationData.planIds,
    maxStartDate = integrationData.maxStartDate,
    minStartDate = integrationData.minStartDate,
    navigateToSectionProps = integrationData.navigateToSectionProps,
    navigateTo = integrationData.navigateTo,
    navigateToPageProps = integrationData.navigateToPageProps,
    biOptions = integrationData.biOptions,
    verticalStatusContent = integrationData.verticalStatusContent;
  const options = {};
  if (title) {
    options.title = title;
  }
  if (subtitle) {
    options.subtitle = subtitle;
  }
  if (planIds) {
    options.planIds = planIds;
  }
  if (biOptions) {
    options.biOptions = biOptions;
  }
  if (maxStartDate) {
    options.checkout ??= {};
    options.checkout.maxStartDate = maxStartDate;
  }
  if (minStartDate) {
    options.checkout ??= {};
    options.checkout.minStartDate = minStartDate;
  }
  if (verticalStatusContent) {
    options.checkout ??= {};
    options.checkout.thankYouPage ??= {};
    options.checkout.thankYouPage.content = {
      title: verticalStatusContent == null ? void 0 : verticalStatusContent.titleText,
      message: verticalStatusContent == null ? void 0 : verticalStatusContent.contentText,
      cta: verticalStatusContent == null ? void 0 : verticalStatusContent.buttonText
    };
  }
  if (navigateToSectionProps) {
    options.checkout ??= {};
    options.checkout.thankYouPage ??= {};
    options.checkout.thankYouPage.navigation = {
      type: 'section',
      options: navigateToSectionProps
    };
  } else if (navigateTo) {
    options.checkout ??= {};
    options.checkout.thankYouPage ??= {};
    options.checkout.thankYouPage.navigation = {
      type: 'url',
      url: navigateTo
    };
  } else if (navigateToPageProps) {
    options.checkout ??= {};
    options.checkout.thankYouPage ??= {};
    options.checkout.thankYouPage.navigation = {
      type: 'page',
      pageId: navigateToPageProps
    };
  }
  return options;
}
export function integrationDataToAppSectionParams(integrationData) {
  const planIds = integrationData.planIds,
    navigateToPageProps = integrationData.navigateToPageProps,
    navigateToSectionProps = integrationData.navigateToSectionProps,
    biOptions = integrationData.biOptions,
    verticalStatusContent = integrationData.verticalStatusContent,
    rest = _objectWithoutPropertiesLoose(integrationData, _excluded2);
  return new URLSearchParams({
    appSectionParams: JSON.stringify({
      ...rest,
      planIds: planIds == null ? void 0 : planIds.join(','),
      biOptions: biOptions && encodeBase64(biOptions),
      navigateToSectionProps: navigateToSectionProps && encodeBase64(navigateToSectionProps),
      navigateToPageProps: navigateToPageProps && btoa(navigateToPageProps),
      verticalStatusContent: verticalStatusContent && encodeBase64(verticalStatusContent)
    })
  });
}
//# sourceMappingURL=integration-data.js.map